import apiEndPoints from 'services/apiEndPoints';
import makeApiRequest, { ChatBotInstance } from 'services/makeApiRequest';
import createQueryParamsForGetReq from 'helper/createQueryParamsForGetReq';

function clearAxiosAuthDefaults() {
  try {
    delete makeApiRequest.defaults.headers.common.Authorization;
  } catch (e) {}

  try {
    delete ChatBotInstance.defaults.headers.common.Authorization;
  } catch (e) {}
}

export function clearSession(authDispatch) {
  try {
    localStorage.clear();
  } catch (e) {}

  if (typeof authDispatch === 'function') {
    authDispatch({ type: 'LOGOUT' });
  }

  try {
    sessionStorage.clear();
  } catch (e) {}

  clearAxiosAuthDefaults();
}

export function persistAccessToken(token) {
  localStorage.setItem('token', JSON.stringify(token));
}

function isAuthOrPermissionError(error) {
  const status = error?.response?.status;
  return status === 401 || status === 403;
}

async function tryFetchAdminProfile(token) {
  const res = await makeApiRequest.get(apiEndPoints.ADMIN_PROFILE, {
    headers: { Authorization: `jwt ${token}` },
  });
  return res?.data;
}

async function tryFetchEmployeeProfile(token) {
  const url = createQueryParamsForGetReq(apiEndPoints.EMPLOYEE_PERSONAL_INFO_API);
  const res = await makeApiRequest.get(url, {
    headers: { Authorization: `jwt ${token}` },
  });
  return res?.data;
}

function mapAdminToLoginPayload(adminData, token) {
  const record = adminData?.data?.[0] ?? adminData?.data ?? adminData ?? {};
  return {
    token,
    email: record?.email ?? record?.user__email ?? null,
    name: record?.first_name ?? record?.name ?? record?.username ?? null,
    is_admin: true,
    is_subadmin: record?.is_subadmin ?? null,
    is_super_admin: record?.is_super_admin ?? null,
    organisation_id: record?.organisation_id ?? null,
    employee_id: record?.employee_id ?? null,
    profile_picture: record?.profile_picture ?? null,
    user_id: record?.user_id ?? record?.id ?? null,
    currency: record?.currency ?? null,
    company_logo: record?.company_logo ?? null,
    company_id: record?.company_id ?? null,
    first_login: record?.first_login ?? null,
    user_permissions: record?.user_permissions ?? null,
  };
}

function mapEmployeeToLoginPayload(employeeData, token) {
  const record = employeeData?.data?.[0] ?? employeeData?.data ?? employeeData ?? {};
  return {
    token,
    email: record?.user__email ?? record?.email ?? null,
    name: record?.user__first_name ?? record?.first_name ?? null,
    is_admin: false,
    is_subadmin: false,
    is_super_admin: false,
    organisation_id: record?.organisation_id ?? null,
    employee_id: record?.employee_id ?? null,
    profile_picture: record?.profile_picture ?? null,
    user_id: record?.user_id ?? record?.id ?? null,
    currency: record?.currency ?? null,
    company_logo: record?.company_logo ?? null,
    company_id: record?.company_id ?? null,
    first_login: record?.first_login ?? null,
    user_permissions: record?.user_permissions ?? null,
  };
}

export async function fetchCurrentUserAndRole(token) {
  try {
    const admin = await tryFetchAdminProfile(token);
    return { role: 'admin', payload: mapAdminToLoginPayload(admin, token) };
  } catch (err) {
    if (!isAuthOrPermissionError(err)) throw err;
  }

  try {
    const employee = await tryFetchEmployeeProfile(token);
    return {
      role: 'employee',
      payload: mapEmployeeToLoginPayload(employee, token),
    };
  } catch (err) {
    throw err;
  }
}

/**
 * Decode the URL-safe base64 session payload produced by the Ping auth service.
 * Returns the parsed object, or null if decoding fails.
 */
export function decodeHrmsSession(encoded) {
  if (!encoded || typeof encoded !== 'string') return null;
  try {
    // Convert URL-safe base64 → standard base64, then restore padding
    const standard = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = standard + '='.repeat((4 - (standard.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Build the LOGIN payload directly from the HRMS session object returned by
 * the Ping auth service. This mirrors the shape expected by authContext LOGIN.
 */
function mapHrmsSessionToLoginPayload(session, token) {
  return {
    token,
    email: session.email ?? null,
    name: session.name ?? null,
    is_admin: session.is_admin ?? false,
    is_subadmin: session.is_subadmin ?? false,
    is_super_admin: session.is_super_admin ?? false,
    employee_id: session.employee_id ?? null,
    profile_picture: session.profile_picture ?? null,
    user_id: session.user_id ?? null,
    currency: session.currency ?? null,
    organisation_id: session.organisation_id ?? null,
    company_logo: session.company_logo ?? null,
    company_id: session.company_id ?? null,
    first_login: session.first_login ?? null,
    user_permissions: session.user_permissions ?? null,
  };
}

/**
 * Bootstrap the HRMS session from a Ping SSO redirect.
 *
 * @param {string} token       - The HRMS JWT from the URL path (/login/:token)
 * @param {Function} authDispatch
 * @param {string|null} encodedSession - Optional base64 session payload from ?p=
 *   When present the full login payload is decoded directly, skipping extra API
 *   calls. When absent (or decoding fails) falls back to fetching the profile.
 */
export async function setTokenAndBootstrapSession(token, authDispatch, encodedSession = null) {
  clearSession(authDispatch);
  persistAccessToken(token);

  try {
    const session = encodedSession ? decodeHrmsSession(encodedSession) : null;

    if (session && session.email) {
      // Fast path: full payload carried in the redirect URL – no extra API calls needed.
      const payload = mapHrmsSessionToLoginPayload(session, token);
      authDispatch({ type: 'LOGIN', payload });
    } else {
      // Fallback: reconstruct session by fetching HRMS profile endpoints.
      const { payload } = await fetchCurrentUserAndRole(token);
      authDispatch({ type: 'LOGIN', payload });
    }
  } catch (err) {
    clearSession(authDispatch);
    throw err;
  }
}
